// ═══════════════ ROUTE SELECTION SERVICE ═══════════════
// Selects the best route based on total cost to rider (fare + tolls)
// Shows recommended route + best alternative for rider to choose

import { estimateFare, computeCtp } from './fareService';
import { detectTollsInRoute } from './tollService';

// ─────────────────────────────────────────────────────────────────────────────
// REAL toll pricing via the Google Routes API (Route.computeRoutes).
// Toll price estimates are supported in Australia. Requires the Routes API to be
// enabled on the Maps key; toll requests are billed at a higher rate.
// If the Routes library/API is unavailable for any reason we fall back to the
// estimate-based path below, so routing never hard-fails.
// ─────────────────────────────────────────────────────────────────────────────

function parseMoney(m) {
  if (!m) return null;
  const units = Number(m.units != null ? m.units : 0);
  const nanos = Number(m.nanos != null ? m.nanos : 0);
  return { currencyCode: m.currencyCode || m.currency || null, value: Math.round((units + nanos / 1e9) * 100) / 100 };
}

// Extract a single AUD toll amount from a Routes API tollInfo object.
// Returns a number, or null if no usable price is present.
function extractTollAUD(tollInfo) {
  if (!tollInfo) return null;
  const arr = tollInfo.estimatedPrice || tollInfo.estimatedPrices || tollInfo.estimated_price;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const parsed = arr.map(parseMoney).filter(Boolean);
  if (parsed.length === 0) return null;
  const pick = parsed.find((p) => p.currencyCode === 'AUD') || parsed[0];
  return pick.value;
}

// Choose recommended + alternative on a time-vs-money basis:
// default to the FASTEST route, but if a cheaper route is within
// TIME_TOLERANCE_MIN of the fastest, prefer the cheaper one.
const TIME_TOLERANCE_MIN = 5;

function selectRoutes(scored) {
  if (!scored || scored.length === 0) return { routes: [], recommended: null, alternative: null };

  const fastest = scored.reduce((a, b) => (b.durationMin < a.durationMin ? b : a));
  const withinTolerance = scored.filter((r) => r.durationMin <= fastest.durationMin + TIME_TOLERANCE_MIN);
  // Cheapest (fare + toll) among routes that aren't meaningfully slower than the fastest
  const recommended = withinTolerance.reduce((a, b) => (b.totalCost < a.totalCost ? b : a));

  let alternative = null;
  const others = scored.filter((r) => r !== recommended);
  if (others.length) {
    // The alternative is the next-best of Google's actual routes, by the same
    // time-vs-money rule: cheapest among those within tolerance of the fastest;
    // otherwise the fastest remaining route.
    const inTol = others.filter((r) => r.durationMin <= fastest.durationMin + TIME_TOLERANCE_MIN);
    alternative = inTol.length
      ? inTol.reduce((a, b) => (b.totalCost < a.totalCost ? b : a))
      : others.reduce((a, b) => (b.durationMin < a.durationMin ? b : a));
  }

  const rest = scored.filter((r) => r !== recommended && r !== alternative);
  const routes = [recommended, ...(alternative ? [alternative] : []), ...rest];

  if (recommended && alternative) {
    recommended.savingsVsAlt = Math.round((alternative.totalCost - recommended.totalCost) * 100) / 100;
    alternative.extraCostVsRec = Math.round((alternative.totalCost - recommended.totalCost) * 100) / 100;
    alternative.timeDiff = alternative.durationMin - recommended.durationMin;
  }
  return { routes, recommended, alternative };
}

function scoreModernRoute(route, index, { tier, fareRates, peakMultiplier, stops, pickup }) {
  const distanceKm = Math.round((route.distanceMeters || 0) / 100) / 10;
  const durationMin = Math.round((route.durationMillis || 0) / 1000 / 60);

  // Real toll price from Google. Only treat the route as tolled when a positive
  // price actually parses — Google may attach an empty toll object to toll-free
  // routes, which must not be shown as a $0.00 toll.
  const tollInfo = route.travelAdvisory && route.travelAdvisory.tollInfo;
  const tollAmt = extractTollAUD(tollInfo);
  let totalToll = 0;
  let hasTolls = false;
  let tolls = [];
  if (tollAmt != null && tollAmt > 0) {
    hasTolls = true;
    totalToll = tollAmt;
    tolls = [{ id: 'tolls', name: 'Tolls', cost: totalToll, isPeak: false, isWeekend: false, estimate: true }];
  } else if (tollInfo && tollAmt == null && typeof window !== 'undefined' && !window.__reservoTollShapeLogged) {
    window.__reservoTollShapeLogged = true;
    try { console.debug('[routeService] tollInfo present but no parseable AUD price:', JSON.stringify(tollInfo)); }
    catch (e) { console.debug('[routeService] tollInfo present, unserializable:', tollInfo); }
  }

  // NSW CTP (Green Slip) per-km charge, region-judged by pickup coords. Mirrors BookingWizard so
  // rec.fare (used directly by the ActiveTrip modify preview) matches the charged fare.
  const ctp = computeCtp(distanceKm, pickup && pickup.lat, pickup && pickup.lng, fareRates && fareRates.ctp);
  const fare = estimateFare({
    distanceKm, durationMin, tier: tier || 'go', peakMultiplier, tolls: totalToll, ctp, rates: fareRates, stops,
  });

  // Convert path literals to LatLng objects so callers can use .lat()/.lng()
  const path = (route.path || []).map((pt) => {
    const lat = typeof pt.lat === 'function' ? pt.lat() : pt.lat;
    const lng = typeof pt.lng === 'function' ? pt.lng() : pt.lng;
    return new window.google.maps.LatLng(lat, lng);
  });

  return {
    index,
    summary: route.description || `Route ${index + 1}`,
    distanceKm,
    durationMin,
    path,
    bounds: route.viewport || null,
    legs: route.legs || [],
    tolls,
    totalToll,
    tollsAreEstimated: true,
    fare,
    totalCost: Math.round(fare.totalFare * 100) / 100,
    score: fare.totalFare,
    hasTolls,
    warnings: [],
  };
}

async function computeRoutesModern({ pickup, destination, pickupTime, tier, fareRates, peakMultiplier = 0, stops = [] }) {
  if (!window.google || !window.google.maps || typeof window.google.maps.importLibrary !== 'function') {
    throw new Error('Maps importLibrary unavailable');
  }
  const { Route } = await window.google.maps.importLibrary('routes');

  const intermediates = (Array.isArray(stops) ? stops : [])
    .filter((s) => s.location && typeof s.location.lat === 'number' && typeof s.location.lng === 'number')
    .map((s) => ({ location: { lat: s.location.lat, lng: s.location.lng } }));

  const baseReq = {
    origin: { location: { lat: pickup.lat, lng: pickup.lng } },
    destination: { location: { lat: destination.lat, lng: destination.lng } },
    travelMode: 'DRIVING',
    routingPreference: 'TRAFFIC_AWARE',
    departureTime: pickupTime > new Date() ? pickupTime : new Date(),
    extraComputations: ['TOLLS'],
    routeModifiers: { vehicleInfo: { emissionType: 'GASOLINE' } },
    fields: ['path', 'description', 'distanceMeters', 'durationMillis', 'routeLabels', 'viewport', 'legs', 'travelAdvisory'],
  };
  if (intermediates.length > 0) baseReq.intermediates = intermediates;
  else baseReq.computeAlternativeRoutes = true;

  const res = await Route.computeRoutes(baseReq);
  const routes = res && res.routes;
  if (!routes || routes.length === 0) throw new Error('No routes returned');

  const scored = routes.map((r, i) => scoreModernRoute(r, i, { tier, fareRates, peakMultiplier, stops, pickup }));


  return selectRoutes(scored);
}

// Public entry point: try real Routes API pricing, fall back to estimates.
export async function getRoutesWithScoring(opts) {
  if (!window.google || !opts.pickup?.lat || !opts.destination?.lat) return { routes: [], recommended: null };
  try {
    return await computeRoutesModern(opts);
  } catch (e) {
    console.warn('[routeService] Real toll pricing unavailable, using estimate fallback:', (e && e.message) || e);
    return await computeRoutesLegacy(opts);
  }
}

// ═══════════════ GET ALL ROUTES WITH SCORING ═══════════════
async function computeRoutesLegacy({ pickup, destination, pickupTime, tier, fareRates, peakMultiplier = 0, stops = [], optimizeWaypoints = false }) {
  if (!window.google || !pickup?.lat || !destination?.lat) return { routes: [], recommended: null };

  // Build waypoints from intermediate stops (Batch 11)
  const waypoints = Array.isArray(stops) && stops.length > 0
    ? stops
        .filter((s) => s.location && typeof s.location.lat === 'number' && typeof s.location.lng === 'number')
        .map((s) => ({ location: new window.google.maps.LatLng(s.location.lat, s.location.lng), stopover: true }))
    : [];

  const directionsService = new window.google.maps.DirectionsService();

  // Promisified single Directions request
  const requestRoutes = (extra = {}) => new Promise((resolve) => {
    const params = {
      origin: { lat: pickup.lat, lng: pickup.lng },
      destination: { lat: destination.lat, lng: destination.lng },
      travelMode: 'DRIVING',
      drivingOptions: {
        departureTime: pickupTime > new Date() ? pickupTime : new Date(),
        trafficModel: 'bestguess',
      },
      avoidTolls: false,
      ...extra,
    };
    if (waypoints.length > 0) {
      params.waypoints = waypoints;
      if (optimizeWaypoints) params.optimizeWaypoints = true;
    }
    directionsService.route(params, (response, status) => {
      resolve(status === 'OK' && response && response.routes && response.routes.length ? response : null);
    });
  });

  // Score one Google route into our route shape
  const scoreRoute = (route, index) => {
    let totalDistanceMeters = 0;
    let totalDurationSeconds = 0;
    const perLegMetrics = [];
    for (const leg of route.legs) {
      totalDistanceMeters += leg.distance.value;
      const legSec = leg.duration_in_traffic?.value || leg.duration.value;
      totalDurationSeconds += legSec;
      perLegMetrics.push({
        distanceKm: Math.round(leg.distance.value / 100) / 10,
        durationMin: Math.round(legSec / 60),
        startAddress: leg.start_address,
        endAddress: leg.end_address,
      });
    }
    const distanceKm = Math.round(totalDistanceMeters / 100) / 10;
    const durationMin = Math.round(totalDurationSeconds / 60);

    const detectedTolls = detectTollsInRoute(route, pickupTime);
    const totalToll = Math.round(detectedTolls.reduce((s, t) => s + t.cost, 0) * 100) / 100;

    const ctp = computeCtp(distanceKm, pickup && pickup.lat, pickup && pickup.lng, fareRates && fareRates.ctp);
    const fare = estimateFare({
      distanceKm, durationMin, tier: tier || 'go', peakMultiplier, tolls: totalToll, ctp, rates: fareRates, stops,
    });
    const totalCostToRider = fare.totalFare;

    return {
      index,
      summary: route.summary || `Route ${index + 1}`,
      distanceKm,
      durationMin,
      perLegMetrics,
      waypointOrder: route.waypoint_order || null,
      tolls: detectedTolls,
      totalToll,
      tollsAreEstimated: detectedTolls.some((t) => t.estimate),
      fare,
      totalCost: Math.round(totalCostToRider * 100) / 100,
      score: totalCostToRider,
      path: route.overview_path,
      bounds: route.bounds,
      legs: route.legs,
      warnings: route.warnings || [],
      hasTolls: totalToll > 0,
    };
  };

  const costSort = (a, b) => {
    // Within $2, prefer the faster route
    if (Math.abs(a.totalCost - b.totalCost) <= 2) return a.durationMin - b.durationMin;
    return a.score - b.score;
  };

  // Main request — alternatives only when there are no waypoints (Google ignores them otherwise)
  const mainResp = await requestRoutes(waypoints.length === 0 ? { provideRouteAlternatives: true } : {});
  if (!mainResp) return { routes: [], recommended: null };

  const scoredRoutes = mainResp.routes.map((r, i) => scoreRoute(r, i));

  // If we have tolled routes but no toll-free one, fetch an explicit toll-free route
  // so the rider always gets a genuine toll-minimising option.
  const anyTolls = scoredRoutes.some((r) => r.hasTolls);
  const haveTollFree = scoredRoutes.some((r) => !r.hasTolls);
  if (anyTolls && !haveTollFree) {
    const tfResp = await requestRoutes({ avoidTolls: true, provideRouteAlternatives: false });
    const tf = tfResp && tfResp.routes && tfResp.routes[0] ? scoreRoute(tfResp.routes[0], scoredRoutes.length) : null;
    if (tf && !tf.hasTolls) {
      tf.summary = `${tf.summary} (toll-free)`;
      tf.isTollFree = true;
      scoredRoutes.push(tf);
    }
  }

  const { routes: rankedRoutes, recommended, alternative } = selectRoutes(scoredRoutes);

  return { routes: rankedRoutes, recommended, alternative, directionsResponse: mainResp };
}


// ═══════════════ ROUTE DISPLAY HELPERS ═══════════════
export function getRouteLabel(route) {
  if (route.hasTolls) return `via ${route.summary} (toll)`;
  return `via ${route.summary} (no toll)`;
}

export function getRouteSavingsText(recommended, alternative) {
  if (!recommended || !alternative) return '';
  const diff = alternative.totalCost - recommended.totalCost;
  if (diff > 0) return `Save $${diff.toFixed(2)} with recommended route`;
  if (diff < 0) return `Alternative is $${Math.abs(diff).toFixed(2)} cheaper`;
  return 'Same total cost';
}

export default { getRoutesWithScoring, getRouteLabel, getRouteSavingsText };